import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { build, pool } from '@fitogest/reactive-form-builder/utils';
import {
    FormType,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { BaseForm } from '../../../../base-form';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ControlSanitarioModel } from '../../../../../models/tareas/control-sanitario.model';
import { ControlSanitarioFormDefinition } from './form-control/form-definition';
import { environment } from '../../../../../../environments/environment';

@Component({
    selector: 'app-control-sanitario-form',
    standalone: false,
    templateUrl: './control-sanitario-form.component.html',
    styleUrls: ['./control-sanitario-form.component.scss']
})
export class ControlSanitarioFormComponent extends BaseForm<ControlSanitarioModel> implements OnInit {
    public readonly reactiveFormLoadingLabel = 'Preparando formulario de control sanitario...';

    public form!: ControlSanitarioFormDefinition;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public loadingData = true;
    public getRequest = this.tareasApi.controlSanitario.GET;
    public readonly serverUrl = environment.serverUrl;
    public initialPreviousData: ControlSanitarioModel | null = null;
    public reactiveFormRecordId = '';

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public tareasApi: TareasApiService,
        public fincasApi: FincasApiService,
        public override router: Router,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.controlSanitario.PUT,
            tareasApi.controlSanitario.POST,
            'control-sanitario',
            'Actualizar Control Sanitario',
            'Realizar Control Sanitario',
            'Duplicar Control Sanitario'
        );
    }

    public async ngOnInit(): Promise<void> {
        try {
            this.loadingData = true;
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId();
            const initialModel = this.initializeHostModel(this.toControlSanitarioModel(this.getModel() ?? {}));

            this.initialPreviousData = this.getRegisterId() ? { ...initialModel } : null;
            this.form = new ControlSanitarioFormDefinition(this.getType(), initialModel);
            this.form.model = this.toControlSanitarioModel(this.model);

            await this.initFormData();
            this.syncModelFromForm();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            console.error('Error loading control sanitario form data.', error);
        }
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<ControlSanitarioModel>): void {
        const stableValue = this.captureStableValueChange(event.value as ControlSanitarioModel);

        this.form.model = this.toControlSanitarioModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<ControlSanitarioModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }

        const stableValue = this.captureStableValueChange(event.value as ControlSanitarioModel);

        this.form.model = this.toControlSanitarioModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();
    }

    private handleUserValueChange(
        formModel: ReactiveFormValue<ControlSanitarioModel>,
        changedField: string | number | symbol | null | undefined,
    ): void {
        this.form.model = this.toControlSanitarioModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();

        if (String(changedField ?? '') === 'id_finca') {
            this.refreshReactiveDropdownMappings();
        }
    }

    private async initFormData(): Promise<void> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        await pool([
            build(this.fincasApi.fincasGET, this.form.fincas, 'nombre', true),
        ], () => this.refreshReactiveDropdownMappings());

        this.applyCreationDefaults();
    }

    private markReactiveFormReady(): void {
        this.loadingData = false;
        this.isReactiveFormReady = true;
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toControlSanitarioModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toControlSanitarioModel(
        sourceModel: Partial<ControlSanitarioModel> | ReactiveFormValue<ControlSanitarioModel>,
    ): ControlSanitarioModel {
        const normalizedModel: Record<string, unknown> = {
            ...(sourceModel as unknown as Record<string, unknown>),
        };

        return normalizedModel as unknown as ControlSanitarioModel;
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim().length > 0) {
            return error.message;
        }

        if (typeof error === 'string' && error.trim().length > 0) {
            return error;
        }

        return 'No se ha podido preparar el formulario de control sanitario.';
    }

    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }

    private applyCreationDefaults(): void {
        if (this.getType() !== FormType.CREATION || this.form.model.id_finca || this.form.fincas.values.length !== 1) {
            return;
        }

        this.form.model = this.toControlSanitarioModel({
            ...this.form.model,
            id_finca: this.form.fincas.values[0]?.value?.id ?? null,
        });
    }

}
